// ═══════════════════════════════════════════════════════════════════
// laostreModel3D.jsx — LAOSTRE, SCULPTED   (rebuilt in Blender, caP'n 2026-09-23)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "time to make Laostre … use all the notes we have … make him the
// same height as Venus, no taller than her". In Terror the male player
// (player_m) IS Laostre. 3D only; 2D screens keep their pictures.
//
// `public/models/laostre.glb`, master `gamemaster/models/sculpt/glb/laostre.glb`,
// builder `gamemaster/models/sculpt/builders/laostre_blender/` (V'ael's
// pipeline, the third Blender person after Lyraei and Venus). It replaces the
// 2026-09-16 SDF body (_scratch/laostre3d, kept beside it as laostre_v1_sdf.glb).
// 22 named meshes on the same 18-bone skeleton Lyraei's runtime has always posed.
//
// ⭐ HIS REST POSE IS THE ART'S POSE — hands in the coat pockets, elbows out.
// The hands are modelled and live INSIDE the pockets (the pipe idle will bring
// the right one out later); the runtime only adds small deltas, never the old
// v1 trick of folding the forearm and scaling the hand to nothing.
// ⭐ THE HEIGHT CONTRACT: his hair tops out at MODEL_TOP 1.634 ≤ Venus's 1.636,
// and plazaFolk3D stands him at AEN_M_H = VENUS_H, so he is exactly her size.
// ⚠ laostrePose is MIRRORED in the builder (lao_export.laostre_pose) for the
// skin-stretch test — change both.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

export const LAOSTRE_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/laostre.glb`;
const MODEL_TOP = 1.634;             // measured at export (Blender build, 2026-09-23: 1.6338): the hair's crest
// THE INK HULL'S WIDTH, PER PART — mirrored in the builder's lao_look.py INK_BY_PART.
// Thin parts get a narrower line (a full 3.2 mm hull is wider than a 6 mm button).
const INK_W = 0.0032;
const INK_BY_PART = [[/buttons/, 0], [/gauges|shirtcuffs|straps/, 0.0016], [/belt/, 0.0018]];
const inkWidth = (name) => (INK_BY_PART.find(([re]) => re.test(name)) || [null, INK_W])[1];
const SELF_LIGHT = 0.42;

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};
const INK = new Map();               // one material per width
const inkMat = (w) => {
  let m = INK.get(w);
  if (m) return m;
  m = new THREE.MeshBasicMaterial({ color: 0x161114, side: THREE.BackSide });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace(
      "#include <begin_vertex>",
      "#include <begin_vertex>\n  transformed += normalize( normal ) * " + w.toFixed(5) + ";"
    );
  };
  // ⚠ two widths share one program signature: without this key the second silently reuses the first
  m.customProgramCacheKey = () => "laostreInk" + w;
  INK.set(w, m);
  return m;
};

function prepare(scene) {
  const root = cloneSkinned(scene);              // a plain clone would share one skeleton between bodies
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side,
      emissive: m.color.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
    });
    o.frustumCulled = false;                     // skinned bounds don't follow the pose
    const w = inkWidth(o.name);
    if (w > 0) {
      const ink = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, inkMat(w)) : new THREE.Mesh(o.geometry, inkMat(w));
      if (o.isSkinnedMesh) ink.bind(o.skeleton, o.bindMatrix);
      ink.name = o.name + "_ink"; ink.frustumCulled = false;
      o.parent.add(ink);
    }
  });
  const B = {};
  root.traverse((o) => { if (o.isBone) B[o.name] = o; });
  if (B.hips) B.hips.userData.y0 = B.hips.position.y;
  return { root, B };
}

// hands stay in the pockets: the walk is the legs, a hip sway and a counter-turn of
// the chest; the arms barely move (a man strolling with his hands in his coat)
function laostrePose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const sw = Math.sin(ph), still = 1 - amt;
  for (const [k, s, off] of [["L", 1, 0], ["R", -1, Math.PI]]) {
    const lp = ph + off, fwd = Math.sin(lp), swing = Math.max(0, Math.cos(lp));
    set("thigh" + k, (-fwd * 0.34 - swing * 0.12) * amt, 0, 0);
    set("shin" + k, swing * 0.55 * amt, 0, 0);
    set("foot" + k, (-swing * 0.28 + Math.max(0, -fwd) * 0.14) * amt, 0, 0);
    set("upperarm" + k, fwd * 0.04 * amt, 0, s * 0.012 * Math.sin(t * 0.8) * still);
    set("forearm" + k, 0, 0, 0);
    set("hand" + k, 0, 0, 0);
  }
  set("hips", 0, sw * 0.07 * amt, Math.sin(t * 0.45) * 0.012 * still);
  if (B.hips) B.hips.position.y = B.hips.userData.y0 + (Math.abs(Math.cos(ph)) - 0.6) * 0.018 * amt;
  set("spine", 0.02 * amt, -sw * 0.05 * amt, 0);
  set("chest", Math.sin(t * 1.2) * 0.010, -sw * 0.04 * amt, 0);         // no lean: the chest carries the hands, the hips carry the pockets
  set("neck", 0.04, 0, 0);
  set("head", -0.04 + Math.sin(t * 0.6) * 0.015, Math.sin(t * 0.3) * 0.22 * (1 - 0.6 * amt), -0.02);
}

export function LaostreModel({ ctl, h = 1.6 }) {
  const gltf = useLoader(GLTFLoader, LAOSTRE_GLB_URL);
  const { root, B } = useMemo(() => prepare(gltf.scene), [gltf]);
  const g = useRef(); const yaw = useRef(0); const ph = useRef(0); const amt = useRef(0);
  useFrame(({ clock }, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 0.7;
    const f = (ctl && ctl.facing) || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9);
    o.rotation.y = yaw.current;
    const speed = (ctl && ctl.speed) || 0;
    amt.current += ((speed ? 1 : 0) - amt.current) * Math.min(1, dt * 6);
    ph.current += (speed ? 1 : amt.current) * dt * 6.5;
    laostrePose(B, t, ph.current, amt.current);
  });
  return (
    <group ref={g}>
      <primitive object={root} scale={h / MODEL_TOP} />
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[laostre] laostre.glb did not load — the painted body stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function LaostreSculpted({ ctl, h, Fallback }) {
  const fb = Fallback ? <Fallback ctl={ctl} h={h} /> : null;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <LaostreModel ctl={ctl} h={h} />
      </Suspense>
    </GlbBoundary>
  );
}

export default LaostreModel;
